import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Loading from "../../Shared/Loading";
import { useQuery } from "../../Hooks/useApi";

const SuperAdminDoctorViewPatient = () => {
  const { id } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState("all");
  const [filterData, setFilterData] = useState([]);

  // Use TanStack Query custom hook for doctor profile details
  const { data: userResponse, loading: userLoading, error } = useQuery(`/user/${id}`);

  // Use TanStack Query custom hook for patients referred by this doctor
  const { data: patientsData, loading: patientsLoading } = useQuery(
    `/user/specific?fieldName=referralId&&fieldValue=${id}`
  );

  const patients = patientsData?.data || [];
  const userData = userResponse?.data;
  const loading = userLoading || patientsLoading;

  function capitalizeFirstLetter(string) {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  }

  useEffect(() => {
    const filter = patients?.filter((user) => {
      const activity = user?.isActive ? "active" : "inActive";
      const matchesSearch = searchInput
        ? user?.name?.toLowerCase()?.includes(searchInput?.toLowerCase()) ||
          user?.email?.toLowerCase()?.includes(searchInput?.toLowerCase()) ||
          user?.phone?.includes(searchInput)
        : true;
      const matchesStatus = selected && selected !== "all" ? activity === selected : true;
      return matchesSearch && matchesStatus;
    });
    setFilterData(filter || []);
  }, [searchInput, selected, patients]);

  if (loading) {
    return <Loading />;
  }
  if (error) return <div className="p-6 text-center text-rose-500 font-bold">Error: {error}</div>;

  return (
    <div className="max-w-6xl mx-auto select-none space-y-6">
      {/* Header bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
            <Icon icon="solar:users-group-two-rounded-bold" className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-lg font-black text-slate-800 tracking-tight">
              {userData?.name || "Doctor"}'s Patients
            </h1>
            <p className="text-xs text-slate-400 font-semibold mt-0.5">
              List of all patients registered under this doctor's referral link
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right hidden sm:block">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Total Patients</p>
            <p className="text-lg font-black text-indigo-600">{patients?.length || 0} Patients</p>
          </div>
          <div className="h-8 w-[1px] bg-slate-100 hidden sm:block"></div>
          <div className="flex gap-2">
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-semibold text-slate-600 bg-white"
            >
              <option value="all">All Patients</option>
              <option value="active">Active</option>
              <option value="inActive">Inactive</option>
            </select>
            <div className="relative">
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search patients..."
                className="w-full pl-8 pr-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-semibold text-slate-600"
              />
              <Icon icon="solar:magnifer-linear" className="absolute left-2.5 top-2.5 text-slate-400 w-4 h-4" />
            </div>
          </div>
        </div>
      </div>

      {/* Table grid */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="overflow-x-auto border border-slate-100 rounded-xl">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/75 border-b border-slate-100">
                <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filterData.length > 0 ? (
                [...filterData].reverse().map((patient) => (
                  <tr key={patient?._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 text-xs font-bold text-slate-800">
                      {capitalizeFirstLetter(patient?.name)}
                    </td>
                    <td className="px-6 py-4 text-xs font-semibold text-indigo-650">
                      {patient?.email}
                    </td>
                    <td className="px-6 py-4 text-xs font-medium text-slate-500">
                      {patient?.phone}
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {patient?.isActive ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-100">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-rose-50 text-rose-700 border border-rose-100">
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs">
                      <div className="flex justify-end items-center gap-2">
                        <Link
                          to={`/superAdminDashboard/superadmin-doctor-patient-report/${patient?._id}`}
                          className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-150 rounded-lg transition-all cursor-pointer"
                          title="View Patient Performance Report"
                        >
                          <Icon icon="solar:graph-bold" className="w-4 h-4" />
                        </Link>
                        <Link
                          to={`/superAdminDashboard/superadmin-patient-edit-profile/${patient?._id}`}
                          className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 border border-slate-200 hover:border-cyan-150 rounded-lg transition-all cursor-pointer"
                          title="Edit Patient Profile"
                        >
                          <Icon icon="basil:edit-outline" className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-10 text-xs text-slate-400">
                    No referred patients found matching filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDoctorViewPatient;
